/**
 * TeamManagement.js
 * Main team collaboration hub — Max plan only.
 * Create org, invite members, manage roles, activity feed.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import {
  Users, UserPlus, Shield, Mail, Clock, Activity,
  Crown, ChevronDown, Copy, Check, Trash2, X,
  AlertTriangle, Building2, Send, Eye, Pencil, Zap,
  UserMinus, RefreshCw, ExternalLink
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const FONT = "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif";

function authHeaders(extra = {}) {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'user_email': userInfo?.email || '',
    'Content-Type': 'application/json',
    ...extra
  };
}

const ROLE_CONFIG = {
  owner:  { label: 'Owner',  color: '#F59E0B', icon: Crown,  desc: 'Full control, billing' },
  admin:  { label: 'Admin',  color: '#8B5CF6', icon: Shield, desc: 'Manage members & settings' },
  member: { label: 'Member', color: '#2B8AFF', icon: Pencil, desc: 'Run checks, export' },
  viewer: { label: 'Viewer', color: '#6B7280', icon: Eye,    desc: 'View dashboards only' },
};

const TeamManagement = ({ onNavigate }) => {
  const { isDarkMode } = useTheme();
  const [org, setOrg] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [subActive, setSubActive] = useState(true);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create org form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgDesc, setOrgDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const colors = {
    bg: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    card: isDarkMode ? '#141516' : '#FFFFFF',
    cardHover: isDarkMode ? '#1A1B1C' : '#FAFAFA',
    border: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
    text: isDarkMode ? '#E2E8F0' : '#1E293B',
    textSec: isDarkMode ? '#64748B' : '#94A3B8',
    accent: '#2563EB',
    accentSecondary: '#D97706',
    danger: '#EF4444',
    success: '#10B981',
    inputBg: isDarkMode ? '#1A1B1C' : '#F9FAFB',
    inputBorder: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
  };

  // ── Data Fetching ──────────────────────────────────────────────────────

  const fetchOrg = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/organizations/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.organization) {
        setOrg(data.organization);
        setMyRole(data.organization.my_role);
        setSubActive(data.organization.subscription_active);
      } else {
        setOrg(null);
      }
    } catch (e) {
      console.error('Failed to fetch org:', e);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    if (!org) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/organizations/${org.id}/members`, { headers: authHeaders() });
      const data = await res.json();
      setMembers(data.members || []);
    } catch (e) { console.error(e); }
  }, [org]);

  const fetchInvitations = useCallback(async () => {
    if (!org) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/organizations/${org.id}/invitations`, { headers: authHeaders() });
      const data = await res.json();
      setInvitations(data.invitations || []);
    } catch (e) { console.error(e); }
  }, [org]);

  const fetchActivity = useCallback(async () => {
    if (!org) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/organizations/${org.id}/activity?limit=50`, { headers: authHeaders() });
      const data = await res.json();
      setActivity(data.activity || []);
    } catch (e) { console.error(e); }
  }, [org]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchOrg();
      setLoading(false);
    };
    init();
  }, [fetchOrg]);

  useEffect(() => {
    if (org) {
      fetchMembers();
      fetchInvitations();
      fetchActivity();
    }
  }, [org, fetchMembers, fetchInvitations, fetchActivity]);

  // ── Actions ────────────────────────────────────────────────────────────

  const createOrg = async () => {
    if (!orgName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/organizations`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: orgName.trim(), description: orgDesc.trim() || null }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create organization');
      }
      await fetchOrg();
      setShowCreateForm(false);
      setOrgName('');
      setOrgDesc('');
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setError(null);
    setInviteSuccess(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/organizations/${org.id}/invitations`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to send invitation');
      }
      const data = await res.json();
      setInviteSuccess(data.invite_url);
      setInviteEmail('');
      fetchInvitations();
    } catch (e) {
      setError(e.message);
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (userId, newRole) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/organizations/${org.id}/members/${userId}/role`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail);
      }
      fetchMembers();
      fetchActivity();
    } catch (e) {
      setError(e.message);
    }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this member from the organization?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/organizations/${org.id}/members/${userId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail);
      }
      fetchMembers();
      fetchActivity();
    } catch (e) {
      setError(e.message);
    }
  };

  const revokeInvite = async (invId) => {
    try {
      await fetch(`${API_BASE_URL}/api/team/organizations/${org.id}/invitations/${invId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      fetchInvitations();
    } catch (e) { console.error(e); }
  };

  const copyInviteUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  // ── Shared styles ──────────────────────────────────────────────────────

  const cardStyle = {
    background: colors.card,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '20px',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: `1px solid ${colors.inputBorder}`,
    background: colors.inputBg,
    color: colors.text,
    fontSize: '13px',
    fontFamily: FONT,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const btnPrimary = {
    padding: '11px 22px',
    borderRadius: '100px',
    border: 'none',
    background: isDarkMode ? '#f0ede4' : '#1a1a1a',
    color: isDarkMode ? '#0D0E0E' : '#F8F5EE',
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '-0.005em',
    cursor: 'pointer',
    fontFamily: FONT,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'transform 0.2s ease, background 0.2s ease',
  };

  // ── Loading ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontFamily: FONT }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', color: colors.textSec }} />
      </div>
    );
  }

  // ── No org yet ─────────────────────────────────────────────────────────

  if (!org) {
    return (
      <div style={{ fontFamily: FONT, maxWidth: '560px', margin: '0 auto', padding: '40px 20px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            position: 'relative',
            width: '56px', height: '56px', borderRadius: '2px',
            background: `linear-gradient(135deg, ${colors.accent}14, ${colors.accentSecondary}14)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <span style={{ position: 'absolute', top: '-2.5px', left: '-2.5px', width: '5px', height: '5px', borderRadius: '50%', background: colors.accent }} />
            <span style={{ position: 'absolute', top: '-2.5px', right: '-2.5px', width: '5px', height: '5px', borderRadius: '50%', background: '#6b64b6' }} />
            <span style={{ position: 'absolute', bottom: '-2.5px', left: '-2.5px', width: '5px', height: '5px', borderRadius: '50%', background: '#b57558' }} />
            <span style={{ position: 'absolute', bottom: '-2.5px', right: '-2.5px', width: '5px', height: '5px', borderRadius: '50%', background: colors.accentSecondary }} />
            <Users size={24} color={colors.accent} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontSize: '16px', fontWeight: 500, letterSpacing: '-0.01em', color: colors.text, margin: '0 0 6px' }}>
            Team collaboration
          </h2>
          <p style={{ fontSize: '13px', lineHeight: 1.5, color: colors.textSec, margin: 0, maxWidth: '360px', marginLeft: 'auto', marginRight: 'auto' }}>
            Create an organization to invite team members and collaborate on projects.
          </p>
        </div>

        {error && (
          <div style={{ padding: '12px 16px', borderRadius: '8px', background: `${colors.danger}15`, color: colors.danger, fontSize: '13px', marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {!showCreateForm ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button onClick={() => setShowCreateForm(true)} style={btnPrimary}>
              <Building2 size={18} /> Create Organization
            </button>
            <p style={{ fontSize: '12px', color: colors.textSec, marginTop: '12px' }}>
              Requires Max plan subscription
            </p>
          </div>
        ) : (
          <div style={cardStyle}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: colors.textSec, display: 'block', marginBottom: '6px' }}>
                Organization Name *
              </label>
              <input
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="e.g. IPA Zambia, World Bank DIME"
                style={inputStyle}
                autoFocus
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: colors.textSec, display: 'block', marginBottom: '6px' }}>
                Description (optional)
              </label>
              <input
                value={orgDesc}
                onChange={e => setOrgDesc(e.target.value)}
                placeholder="Brief description of your team"
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={createOrg} disabled={creating || !orgName.trim()} style={{ ...btnPrimary, opacity: creating || !orgName.trim() ? 0.5 : 1 }}>
                {creating ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={16} />}
                {creating ? 'Creating...' : 'Create'}
              </button>
              <button onClick={() => setShowCreateForm(false)} style={{ ...btnPrimary, background: 'transparent', color: colors.textSec, border: `1px solid ${colors.border}` }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Org exists — main team hub ─────────────────────────────────────────

  const canManage = myRole === 'owner' || myRole === 'admin';
  const pendingInvites = invitations.filter(i => i.status === 'pending');

  const tabs = [
    { id: 'members', label: 'Members', icon: Users, count: members.length },
    { id: 'invitations', label: 'Invitations', icon: Mail, count: pendingInvites.length },
    { id: 'activity', label: 'Activity', icon: Activity, count: null },
  ];

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString();
  };

  const actionLabel = (action) => {
    const map = {
      'org.created': 'Created organization',
      'org.settings_changed': 'Updated settings',
      'member.invited': 'Sent invitation',
      'member.joined': 'Joined organization',
      'member.role_changed': 'Changed role',
      'member.removed': 'Removed member',
      'member.left': 'Left organization',
      'project.created': 'Created project',
      'project.shared': 'Shared project',
      'data.uploaded': 'Uploaded data',
      'checks.run': 'Ran quality checks',
      'report.generated': 'Generated report',
    };
    return map[action] || action.replace(/\./g, ' ');
  };

  return (
    <div style={{ fontFamily: FONT, maxWidth: '900px', margin: '0 auto', padding: '24px 20px' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* ── Org Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: colors.text, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Building2 size={22} color={colors.accent} /> {org.name}
          </h2>
          <p style={{ fontSize: '13px', color: colors.textSec, margin: 0 }}>
            {org.member_count} member{org.member_count !== 1 ? 's' : ''} · {org.description || 'Team organization'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
            background: subActive ? `${colors.success}15` : `${colors.danger}15`,
            color: subActive ? colors.success : colors.danger,
          }}>
            {subActive ? 'Active' : 'Subscription Expired'}
          </span>
          <span style={{
            padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
            background: `${ROLE_CONFIG[myRole]?.color || '#666'}15`,
            color: ROLE_CONFIG[myRole]?.color || '#666',
          }}>
            {ROLE_CONFIG[myRole]?.label || myRole}
          </span>
        </div>
      </div>

      {!subActive && (
        <div style={{
          padding: '14px 18px', borderRadius: '10px', marginBottom: '20px',
          background: `${colors.danger}10`, border: `1px solid ${colors.danger}30`,
          display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: colors.danger,
        }}>
          <AlertTriangle size={18} />
          <span>Organization subscription has expired. Team access is frozen until the owner renews.</span>
        </div>
      )}

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: '8px', background: `${colors.danger}15`, color: colors.danger, fontSize: '13px', marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <AlertTriangle size={16} /> {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}><X size={14} /></button>
        </div>
      )}

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '0' }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 16px', border: 'none', borderRadius: '8px 8px 0 0',
                background: active ? colors.card : 'transparent',
                color: active ? colors.text : colors.textSec,
                fontWeight: active ? '600' : '400',
                fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
                borderBottom: active ? `2px solid ${colors.accent}` : '2px solid transparent',
                transition: 'all 0.15s ease',
              }}
            >
              <Icon size={16} />
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span style={{
                  padding: '1px 7px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                  background: active ? `${colors.accent}20` : `${colors.textSec}15`,
                  color: active ? colors.accent : colors.textSec,
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Members Tab ── */}
      {activeTab === 'members' && (
        <div>
          {/* Invite bar */}
          {canManage && subActive && (
            <div style={{ ...cardStyle, marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UserPlus size={16} color={colors.accent} /> Invite Team Member
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="colleague@organization.com"
                  style={{ ...inputStyle, flex: '1 1 200px' }}
                  onKeyDown={e => e.key === 'Enter' && inviteMember()}
                />
                <select
                  value={inviteRole}
                  onChange={e => setInviteRole(e.target.value)}
                  style={{ ...inputStyle, width: 'auto', minWidth: '120px', cursor: 'pointer' }}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button onClick={inviteMember} disabled={inviting || !inviteEmail.trim()} style={{ ...btnPrimary, opacity: inviting || !inviteEmail.trim() ? 0.5 : 1 }}>
                  {inviting ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                  Invite
                </button>
              </div>
              {inviteSuccess && (
                <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '8px', background: `${colors.success}10`, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <Check size={16} color={colors.success} />
                  <span style={{ color: colors.success, flex: 1 }}>Invitation sent!</span>
                  <button onClick={() => copyInviteUrl(inviteSuccess)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.accent, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                    {copiedUrl ? <Check size={14} /> : <Copy size={14} />}
                    {copiedUrl ? 'Copied!' : 'Copy link'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Members list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {members.map(m => {
              const role = ROLE_CONFIG[m.role] || ROLE_CONFIG.member;
              const RoleIcon = role.icon;
              return (
                <div key={m.id} style={{
                  ...cardStyle, padding: '14px 18px',
                  display: 'flex', alignItems: 'center', gap: '14px',
                  transition: 'background 0.15s ease',
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '10px',
                    background: `${role.color}15`, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {m.picture ? (
                      <img src={m.picture} alt="" style={{ width: '40px', height: '40px', borderRadius: '10px', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: '13px', fontWeight: '600', color: role.color }}>
                        {(m.name || m.email)?.[0]?.toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.name || m.email.split('@')[0]}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSec }}>{m.email}</div>
                  </div>

                  {/* Role badge */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '4px 10px', borderRadius: '6px',
                    background: `${role.color}12`, color: role.color,
                    fontSize: '12px', fontWeight: '600',
                  }}>
                    <RoleIcon size={12} /> {role.label}
                  </div>

                  {/* Actions */}
                  {canManage && m.role !== 'owner' && (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <select
                        value={m.role}
                        onChange={e => changeRole(m.user_id, e.target.value)}
                        style={{
                          padding: '4px 8px', borderRadius: '6px', fontSize: '11px',
                          border: `1px solid ${colors.border}`, background: colors.inputBg,
                          color: colors.textSec, cursor: 'pointer', fontFamily: FONT,
                        }}
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button onClick={() => removeMember(m.user_id)} style={{
                        padding: '4px 8px', borderRadius: '6px', border: `1px solid ${colors.border}`,
                        background: 'transparent', color: colors.danger, cursor: 'pointer',
                        display: 'flex', alignItems: 'center',
                      }}>
                        <UserMinus size={14} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {members.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textSec, fontSize: '13px' }}>
              No members yet. Invite your team above.
            </div>
          )}
        </div>
      )}

      {/* ── Invitations Tab ── */}
      {activeTab === 'invitations' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {invitations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textSec, fontSize: '13px' }}>
              No invitations sent yet.
            </div>
          ) : (
            invitations.map(inv => (
              <div key={inv.id} style={{ ...cardStyle, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '8px',
                  background: inv.status === 'pending' ? `${colors.accent}15` : inv.status === 'accepted' ? `${colors.success}15` : `${colors.textSec}10`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Mail size={16} color={inv.status === 'pending' ? colors.accent : inv.status === 'accepted' ? colors.success : colors.textSec} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: colors.text }}>{inv.invited_email}</div>
                  <div style={{ fontSize: '12px', color: colors.textSec }}>
                    {inv.role} · invited by {inv.invited_by_name || 'unknown'} · {formatTime(inv.created_at)}
                  </div>
                </div>
                <span style={{
                  padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                  background: inv.status === 'pending' ? `${colors.accent}15` : inv.status === 'accepted' ? `${colors.success}15` : `${colors.textSec}10`,
                  color: inv.status === 'pending' ? colors.accent : inv.status === 'accepted' ? colors.success : colors.textSec,
                  textTransform: 'capitalize',
                }}>
                  {inv.status}
                </span>
                {inv.status === 'pending' && canManage && (
                  <button onClick={() => revokeInvite(inv.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: colors.danger, padding: '4px',
                  }}>
                    <X size={16} />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Activity Tab ── */}
      {activeTab === 'activity' && (
        <div>
          {activity.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textSec, fontSize: '13px' }}>
              No activity recorded yet.
            </div>
          ) : (
            <div style={{ position: 'relative', paddingLeft: '24px' }}>
              {/* Timeline line */}
              <div style={{
                position: 'absolute', left: '8px', top: '8px', bottom: '8px',
                width: '2px', background: colors.border, borderRadius: '1px',
              }} />

              {activity.map((entry, i) => (
                <div key={entry.id} style={{ position: 'relative', marginBottom: '12px', paddingBottom: '4px' }}>
                  {/* Dot */}
                  <div style={{
                    position: 'absolute', left: '-20px', top: '6px',
                    width: '10px', height: '10px', borderRadius: '50%',
                    background: colors.accent, border: `2px solid ${colors.card}`,
                  }} />
                  <div style={{ fontSize: '13px', color: colors.text }}>
                    <span style={{ fontWeight: '600' }}>{entry.user_name || entry.user_email.split('@')[0]}</span>
                    {' '}
                    <span style={{ color: colors.textSec }}>{actionLabel(entry.action)}</span>
                    {entry.project_name && (
                      <span style={{ color: colors.accent }}> · {entry.project_name}</span>
                    )}
                  </div>
                  {entry.description && (
                    <div style={{ fontSize: '12px', color: colors.textSec, marginTop: '2px' }}>
                      {entry.description}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: colors.textSec, marginTop: '2px', opacity: 0.7 }}>
                    {formatTime(entry.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamManagement;